import type { ReactNode } from 'react';
import { FONTS, TOKENS, TYPE } from '../theme';

interface PayloadItem {
  dataKey?: string | number;
  name?: string | number;
  value?: number | string | Array<number | string>;
  color?: string;
  stroke?: string;
  fill?: string;
  payload?: Record<string, unknown>;
}

/**
 * A Recharts tooltip in the site's style: a floating card with a hairline, the
 * x value as a mono label, and each series as swatch, name and a tabular
 * figure. Pass as `<Tooltip content={<ChartTooltip ... />} />`; Recharts
 * fills in `active`, `label` and `payload`.
 */
export function ChartTooltip({
  active,
  label,
  payload,
  formatLabel,
  formatters = {},
  names = {},
  colors = {},
}: {
  active?: boolean;
  label?: ReactNode;
  payload?: PayloadItem[];
  formatLabel?: (label: ReactNode) => ReactNode;
  /** Value formatter per dataKey. */
  formatters?: Record<string, (value: number) => string>;
  /** Display name per dataKey. */
  names?: Record<string, string>;
  /** Swatch colour per dataKey, where the series' own colour is not what to show. */
  colors?: Record<string, string>;
}) {
  if (!active || !payload || payload.length === 0) return null;
  return (
    <div
      style={{
        background: TOKENS.surface3,
        border: `1px solid ${TOKENS.borderStrong}`,
        borderRadius: 10,
        boxShadow: TOKENS.shadowPop,
        padding: '9px 12px 10px',
        minWidth: 140,
        fontFamily: FONTS.body,
      }}
    >
      <div style={{ ...TYPE.monoLabel, fontSize: 10.5, marginBottom: 6 }}>
        {formatLabel ? formatLabel(label) : label}
      </div>
      <div style={{ display: 'grid', gap: 4 }}>
        {payload.map((item) => {
          const key = String(item.dataKey ?? item.name ?? '');
          const raw = Array.isArray(item.value) ? item.value[0] : item.value;
          const num = typeof raw === 'number' ? raw : Number(raw);
          const fmt = formatters[key];
          const swatch = colors[key] ?? item.stroke ?? item.color ?? item.fill ?? TOKENS.text;
          return (
            <div
              key={key}
              style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 16 }}
            >
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 12, color: TOKENS.textSecondary }}>
                <span aria-hidden style={{ width: 8, height: 8, borderRadius: 2, background: swatch }} />
                {names[key] ?? item.name ?? key}
              </span>
              <span style={{ ...TYPE.monoFigure, fontSize: 12.5, color: TOKENS.text }}>
                {fmt && Number.isFinite(num) ? fmt(num) : String(raw ?? '')}
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
}
